import { useRef, useCallback } from "react";

/**
 * Lightweight hook that tracks hard date-validation errors from
 * UnifiedDateInput fields. Each field registers its error state via
 * the returned `onDateValidation(key)` helper (returns a stable
 * onValidation callback for that key). `hasDateErrors()` returns
 * true when any field has a hard error — call it in the save gate.
 *
 * Usage:
 *   const { onDateValidation, hasDateErrors } = useDateErrors();
 *   <UnifiedDateInput ... onValidation={onDateValidation("my-field")} />
 *   const save = () => { if (hasDateErrors()) { toast.error(...); return; } ... };
 */
export function useDateErrors() {
  const errors = useRef({});
  const callbacks = useRef({});

  const onDateValidation = useCallback((key) => {
    if (!callbacks.current[key]) {
      callbacks.current[key] = ({ error }) => {
        if (error) errors.current[key] = error;
        else delete errors.current[key];
      };
    }
    return callbacks.current[key];
  }, []);

  const hasDateErrors = useCallback(() => Object.keys(errors.current).length > 0, []);

  return { onDateValidation, hasDateErrors };
}
